import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import type { FooterConfig } from "@/config/counties/types";
import { SOCIAL_PROFILES } from "@/lib/social-profiles";
import { SUPPORT_EMAIL } from "@/config/contact";
type FooterProps = {
  data: FooterConfig;
  basePath?: string;
};

// Grouped link columns, the structure every established B2B site uses (Apollo,
// ZoomInfo, Cognism). Beyond looking substantial, the grouping is real SEO
// plumbing: it puts a crawlable sitewide link on the comparison and directory
// pages that previously depended on the sitemap alone for discovery, and gives
// each one anchor text describing where it goes.
//
// Every href here must be a route that actually exists — a footer full of dead
// links is the fastest way to look SMALLER than you are. Verified against
// app/ on 2026-08-18.
export const LINK_COLUMNS: {
  heading: string;
  links: {
    label: string;
    href: string;
  }[];
}[] = [{
  heading: "Product",
  links: [{
    label: "Features",
    href: "/#features"
  }, {
    label: "How it works",
    href: "/#process"
  }, {
    label: "Pricing",
    href: "/pricing"
  }, {
    label: "Contact directory",
    href: "/directory"
  }, {
    label: "FAQ",
    href: "/#faq"
  }]
}, {
  heading: "Compare",
  links: [{
    label: "vs ZoomInfo",
    href: "/vs/zoominfo"
  }, {
    label: "vs Apollo",
    href: "/vs/apollo"
  }, {
    label: "vs Sales Navigator",
    href: "/vs/sales-navigator"
  }, {
    label: "vs Lusha",
    href: "/vs/lusha"
  },
  // The sitemap alone never got these crawled ("URL is unknown to Google"
  // days after submission). The footer ships on every page, including the
  // homepage, which is the one URL Google recrawls reliably here.
  {
    label: "Apollo pricing",
    href: "/pricing/apollo"
  }, {
    label: "ZoomInfo pricing",
    href: "/pricing/zoominfo"
  }, {
    label: "Best B2B databases",
    href: "/best-b2b-contact-databases"
  }, {
    label: "All comparisons",
    href: "/vs"
  }, {
    label: "All alternatives",
    href: "/alternatives"
  }]
}, {
  heading: "Company",
  links: [{
    label: "About us",
    href: "/about"
  }, {
    label: "Contact us",
    href: "/contact"
  }, {
    label: "Blog",
    href: "/blog"
  }, {
    label: "Affiliate program",
    href: "/affiliates"
  }, {
    label: "Data honesty",
    href: "/data-honesty"
  }]
}, {
  heading: "Legal",
  links: [{
    label: "Terms of Use",
    href: "/terms"
  }, {
    label: "Privacy Policy",
    href: "/privacy"
  }]
}];
export default function Footer(t0) {
  const $ = _c(24);
  const {
    data,
    basePath: t1
  } = t0;
  const basePath = t1 === undefined ? "/" : t1;
  let t2;
  if ($[0] !== data.brandName) {
    t2 = <Image src="/full-logo.svg" alt={data.brandName} width={195} height={30} className="h-7 w-auto" />;
    $[0] = data.brandName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== basePath || $[3] !== t2) {
    t3 = <Link href={basePath} className="flex items-center">{t2}</Link>;
    $[2] = basePath;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data.tagline) {
    t4 = data.tagline && <p className="text-muted-ink text-sm mt-3 max-w-[280px] leading-relaxed">{data.tagline}</p>;
    $[5] = data.tagline;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-block text-muted-ink text-sm mt-4 hover:text-primary transition-colors">{SUPPORT_EMAIL}</a>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center gap-3.5 mt-5">{SOCIAL_PROFILES.map(_temp)}</div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t4) {
    t7 = <div className="col-span-2 lg:col-span-2">{t3}{t4}{t5}{t6}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = LINK_COLUMNS.map(_temp3);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7) {
    t9 = <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10">{t7}{t8}</div>;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Date().getFullYear();
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== data.brandName) {
    t11 = <p className="text-muted-ink text-sm" suppressHydrationWarning={true}>© {t10} {data.brandName}. All rights reserved.</p>;
    $[16] = data.brandName;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="text-subtle-ink text-xs text-center sm:text-right max-w-[520px]">Pattern-guessed email addresses are always labeled as guesses and never counted as verified contacts.</p>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t11) {
    t13 = <div className="mt-10 pt-6 border-t border-line-soft flex flex-col sm:flex-row items-center justify-between gap-3">{t11}{t12}</div>;
    $[19] = t11;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13 || $[22] !== t9) {
    t14 = <footer className="bg-card border-t border-line-soft"><div className="container mx-auto px-4 sm:px-6 py-12 sm:py-14">{t9}{t13}</div></footer>;
    $[21] = t13;
    $[22] = t9;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
}
function _temp3(column) {
  return <nav key={column.heading} aria-label={column.heading}><h2 className="text-[13px] font-bold text-ink uppercase tracking-wide mb-3.5">{column.heading}</h2><ul className="space-y-2.5">{column.links.map(_temp2)}</ul></nav>;
}
function _temp2(link) {
  return <li key={link.href}><Link href={link.href} className="text-muted-ink text-sm hover:text-primary transition-colors">{link.label}</Link></li>;
}
function _temp(profile) {
  return <a key={profile.url} href={profile.url} target="_blank" rel="noopener noreferrer me" aria-label={profile.name} title={profile.name} className="text-subtle-ink hover:text-primary transition-colors"><svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true" focusable="false"><path d={profile.path} /></svg></a>;
}